import { Controller } from "@hotwired/stimulus";
import { Editor, generateJSON, getHTMLFromFragment } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { BubbleMenu } from "@tiptap/extension-bubble-menu";
import { UniqueID } from "@tiptap/extension-unique-id";
import Autosave from "@custom-tiptap/extensions/autosave";
import Vimeo from "@custom-tiptap/extensions/vimeo";
import { generateElementId, ELEMENT_ID_ATTRIBUTE, ELEMENT_ID_TYPES } from "@custom-tiptap/element_id";
import { t } from "../lib/i18n";
import { PluginKey } from '@tiptap/pm/state'

export default class TiptapEditor extends Controller {
  static targets = ["content", "editor", "bubbleMenu", "changesMenu"];
  static values = { processingAi: { type: Boolean, default: false } };

  async connect() {
    const connection = Symbol("connection");
    this.currentConnection = connection;
    const initialContent = this.contentTarget.value || "";
    const extensionsList = await this.setupEditorExtensions();
    if (this.currentConnection !== connection) { return; }
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: extensionsList,
      content: initialContent,
      editable: true,
      shouldRerenderOnTransaction: true,
      editorProps: {
        attributes: {
          class: "tiptap-editor-container",
        },
      },
      onSelectionUpdate: () => {
        this.refreshButtonStates();
      },
      onUpdate: () => {
        // Both debounced wrappers are built once below and reused so a typing
        // burst collapses into a single trailing call. The previous inline
        // pattern (`debounce(fn, delay)()`) built a fresh wrapper per call,
        // which meant each keystroke spawned its own timer.
        this.debouncedUpdateHiddenField();
        this.debouncedSave();
        this.refreshButtonStates();
        this.refreshChangesMenu();
      },
    });
    const debounce = (fn, delay) => {
      let timeout = null;
      const debounced = function (...args) {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => {
          timeout = null;
          fn.apply(this, args);
        }, delay);
      };
      debounced.cancel = () => {
        window.clearTimeout(timeout);
        timeout = null;
      };
      debounced.flush = () => {
        if (timeout === null) { return; }
        debounced.cancel();
        fn();
      };
      return debounced;
    };
    this.debouncedUpdateHiddenField = debounce(this.updateHiddenField.bind(this), 1000);
    this.debouncedSave = debounce(this.editorCommand("save", {}, false), 3000);
    this.refreshButtonStates();
    this.initializeRenderer();
    this.dispatchReadyEvent();
  }

  disconnect() {
    this.currentConnection = null;
    this.debouncedUpdateHiddenField?.cancel();
    this.debouncedSave?.flush();
    this.editor?.destroy();
  }

  dispatchReadyEvent() {
    this.element.dispatchEvent(new CustomEvent("tiptap:ready", { bubbles: true }));
  }

  async setupEditorExtensions() {
    let extensionsList;

    if (this.isAdvanced()) {
      try {
        const module = await import("@custom-tiptap/tiptap_advanced");
        extensionsList = module.initializeAdvancedExtensions(this);
        module.setupAdvancedCommands(this);
      } catch (error) {
        console.error("Error loading advanced Tiptap extensions:", error);
      }
    } else {
      extensionsList = this.initializeBaseExtensions();
    }

    return extensionsList;
  }

  initializeBaseExtensions() {
    const extensions = [
      StarterKit.configure({
        link: {
          linkOnPaste: false,
          openOnClick: false,
          autolink: false,
          defaultProtocol: "https",
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Image,
      Vimeo,
      TableKit.configure({
        table: {
          HTMLAttributes: {
            class: "table table-bordered table-striped",
          },
        },
      }),
      Autosave.configure({
        autoSaveUrl: this.element.dataset.autosaveUrl,
        autoSave: this.element.dataset.autosave === "true",
      }),
      UniqueID.configure({
        attributeName: ELEMENT_ID_ATTRIBUTE,
        types: ELEMENT_ID_TYPES,
        generateID: generateElementId,
      }),
    ];
    if (this.hasBubbleMenuTarget) {
      extensions.push(
        BubbleMenu.configure({
          pluginKey: new PluginKey('bubbleMenu'),
          element: this.bubbleMenuTarget,
          appendTo: this.editorTarget,
          shouldShow: () => {
            if (this.hasAiChanges() && this.isTrackingAiChanges()) {
              return false;
            }
            this.refreshButtonStates();
            if (this.hasSelection()) {
              return true;
            }
            return false;
          },
          options: {
            onShow: () => {
              this.bubbleMenuTarget.style.display = "block";
              this.refreshButtonStates();
            },
          },
        }),
      );
    }
    return extensions;
  }

  async initializeRenderer() {
    if (this.element.attributes.renderer) {
      try {
        const module = await import("@custom-tiptap/tiptap_renderer");
        module.makeRenderer(this);
      } catch (error) {
        console.error("Failed to load Tiptap renderer. Renderer attribute:", this.element.attributes.renderer, error);
      }
    }
  }

  isAdvanced() {
    if (this.element.attributes.advanced) {
      return this.element.attributes.advanced.value === "true";
    }
    return false;
  }

  hasAiChanges() {
    const aiChanges = this.editor?.storage?.aiChanges?.getChanges?.() || [];
    return aiChanges.length > 0;
  }

  isTrackingAiChanges() {
    return this.editor?.storage?.aiChanges?.getIsTrackingAiChanges?.();
  }

  hasSelection() {
    return document.getSelection().toString().length > 0;
  }

  updateHiddenField() {
    const hiddenField = this.contentTarget;
    if (hiddenField) {
      hiddenField.value = this.editor.getHTML();
    } else {
      console.error("Hidden field not found");
    }
  }

  refreshButtonStates() {
    if (!this.editor) {
      return;
    }
    const buttons = document.querySelectorAll("[data-action^=\"tiptap#\"]");

    const headingToggle = document.querySelector("#headings");
    const formattingToggle = document.querySelector("#formatting");

    if (!this.editor.isFocused) {
      buttons.forEach((button) => {
        button.classList.remove("btn-info");
      });
      if (headingToggle) headingToggle.classList.remove("btn-info");
      if (formattingToggle) formattingToggle.classList.remove("btn-info");
      return;
    }

    if (headingToggle) {
      const isAnyHeadingActive = [1, 2, 3, 4, 5, 6].some((level) =>
        this.editor.isActive("heading", { level })
      );
      headingToggle.classList.toggle("btn-info", isAnyHeadingActive);
    }

    if (formattingToggle) {
      const isAnyHeadingActive = [1, 2, 3, 4, 5, 6].some((level) =>
        this.editor.isActive("heading", { level })
      );
      formattingToggle.classList.toggle("btn-info", isAnyHeadingActive);
    }

    buttons.forEach((button) => {
      const format = button.dataset.format;
      if (!format || format === "paragraph") { return; }

      let formatValue;
      if (["leftAlign", "centerAlign", "rightAlign", "justify"].includes(format)) {
        const position = format.split("Align")[0];
        formatValue = { textAlign: position };
      } else {
        formatValue = format;
      }
      if (format === "heading" && button.dataset.level) {
        const level = parseInt(button.dataset.level, 10);
        button.classList.toggle("btn-info", this.editor.isActive("heading", { level }));
      }
      else if (format !== "heading") {
        button.classList.toggle("btn-info", this.editor.isActive(formatValue));
      }
    });
  }

  refreshChangesMenu = () => {
    if (!this.isAdvanced() || !this.hasChangesMenuTarget) { return; }
    const changesMenu = this.changesMenuTarget;
    if (changesMenu) {
      let changes = this.editor.storage.aiChanges.getChanges();
      const tracking = this.editor.storage.aiChanges.getIsTrackingAiChanges();
      changesMenu.style.display = (changes.length > 0 && tracking) ? 'block' : 'none';
    }
  }

  enableEditor() {
    if (!this.editor) {
      console.error("Editor instance is not initialized.");
      return;
    }
    this.editor.setEditable(true, false);
  }

  disableEditor() {
    if (!this.editor) {
      console.error("Editor instance is not initialized.");
      return;
    }
    this.editor.setEditable(false, false);
  }

  setContent(html, options = {}) {
    console.log("Setting content in Tiptap editor:", html);
    this.editorCommand("setContent", [html, options])();
    this.updateHiddenField();
  }

  getSelectionHtml() {
    const tr = this.editor.state;
    return getHTMLFromFragment(
      tr.doc.slice(tr.selection.from, tr.selection.to).content,
      this.editor.schema
    );
  }

  editorCommand(command, options = {}, focus = true) {
    return (event) => {
      if (event) { event.preventDefault(); }

      if (command === "command" && typeof options === "function") {
        const chain = focus ? this.editor.chain().focus() : this.editor.chain();
        chain.command(options).run();
        return;
      }

      const format = options.format;
      delete options.format;
      const chain = focus ? this.editor.chain().focus() : this.editor.chain();
      if (format && this.editor.isActive(format) && document.getSelection().toString().length === 0) {
        chain.extendMarkRange(format)[command](options).run();
      } else if (typeof options === "object" && !Array.isArray(options)) {
        chain[command](options).run();
      } else if (Array.isArray(options)) {
        chain[command](...options).run();
      }
    };
  }

  // Text formatting commands
  bold = this.editorCommand("toggleBold", { format: "bold" });
  italic = this.editorCommand("toggleItalic", { format: "italic" });
  underline = this.editorCommand("toggleUnderline", { format: "underline" });
  // List commands
  bulletList = this.editorCommand("toggleBulletList");
  orderedList = this.editorCommand("toggleOrderedList");
  // Block-level commands
  paragraph = this.editorCommand("setParagraph");
  blockquote = this.editorCommand("toggleBlockquote");
  codeBlock = this.editorCommand("toggleCodeBlock");
  heading = (event) => {
    const level = event.currentTarget.dataset.level;
    if (level) {
      this.editorCommand("toggleHeading", { level: parseInt(level, 10) })();
    }
  };
  // Utility commands
  horizontalRule = this.editorCommand("setHorizontalRule");
  vimeo = (event) => {
    if (event) { event.preventDefault(); }

    const url = window.prompt(t("views.shared.tiptap.vimeo.prompt", "Paste a Vimeo video URL"), "");
    if (url === null || url.trim() === "") { return; }

    if (this.editor.chain().focus().setVimeoVideo({ src: url }).run()) {
      this.updateHiddenField();
    } else {
      window.alert(t("views.shared.tiptap.vimeo.invalid", "That does not look like a Vimeo video URL."));
    }
  };
  undo = this.editorCommand("undo");
  redo = this.editorCommand("redo");
  logSelection = this.editorCommand("command", ({ tr }) => {
    console.log(this.getSelectionHtml());
    return true;
  });
  logSelectionText = () => {
    console.log(document.getSelection().toString());
  };
  logChanges = () => {
    const storage = this.editor.extensionStorage.aiChanges
    const changes = storage.getChanges()
    console.log("AI Changes:", changes);
  };
  selectParentNode = this.editorCommand("selectParentNode");

  applyEditOperations(operations) {
    if (!Array.isArray(operations) || operations.length === 0) return;

    const positions = this.collectElementIdPositions();
    const sortedOps = [...operations]
      .map((op) => ({ op, anchor: positions.get(op.target) }))
      .filter((entry) => entry.anchor)
      .sort((a, b) => b.anchor.pos - a.anchor.pos);

    if (sortedOps.length === 0) {
      document.dispatchEvent(new CustomEvent("ai-operation-error", {
        detail: { error: "Could not locate any target element id in the document." }
      }));
      return;
    }

    if (typeof this.startAiProcessing === 'function') {
      this.startAiProcessing();
    }

    const tr = this.editor.state.tr;
    try {
      sortedOps.forEach(({ op, anchor }) => {
        const content = op.content ? this.htmlToFragment(op.content) : null;
        switch (op.type) {
          case 'replace':
            if (content) tr.replaceWith(anchor.pos, anchor.pos + anchor.node.nodeSize, content);
            break;
          case 'insert_before':
            if (content) tr.insert(anchor.pos, content);
            break;
          case 'insert_after':
            if (content) tr.insert(anchor.pos + anchor.node.nodeSize, content);
            break;
          case 'delete':
            tr.delete(anchor.pos, anchor.pos + anchor.node.nodeSize);
            break;
        }
      });
    } catch (error) {
      document.dispatchEvent(new CustomEvent("ai-operation-error", {
        detail: { error: `Failed to apply AI edit: ${error.message}` }
      }));
      return;
    }

    if (tr.docChanged) {
      this.editor.view.dispatch(tr);
      this.updateHiddenField();
    }
  }

  collectElementIdPositions() {
    const positions = new Map();
    this.editor.state.doc.descendants((node, pos) => {
      const id = node.attrs && node.attrs["tiptap-element-id"];
      if (id) positions.set(id, { pos, node });
    });
    return positions;
  }

  htmlToFragment(html) {
    const json = generateJSON(html, this.editor.extensionManager.extensions);
    return this.editor.schema.nodeFromJSON(json).content;
  }
  debugReturnSelectionRange = () => {
    const { from, to } = this.editor.state.selection;
    console.log("Selection range:", { from, to });
    return { from, to };
  }
  // Text Alignment commands
  leftAlign = this.editorCommand("toggleTextAlign", ["left"]);
  centerAlign = this.editorCommand("toggleTextAlign", ["center"]);
  rightAlign = this.editorCommand("toggleTextAlign", ["right"]);
  justify = this.editorCommand("toggleTextAlign", ["justify"]);
  // Integration commands
  insertContent = (content) => {
    if (this.editor.state.selection?.empty) {
      this.editorCommand("insertContent", content)();
    } else {
      const cursorPositionFrom = this.editor.state.selection.$from.pos;
      const cursorPositionTo = this.editor.state.selection.$to.pos;
      this.editorCommand("insertContentAt", [{ from: cursorPositionFrom, to: cursorPositionTo }, content])();
    }
  };
  // Advanced commands live in app/frontend/controllers/tiptap_advanced.js
}